import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { getHours, getMinutes, format, startOfDay, addDays, eachDayOfInterval, isBefore, getDaysInMonth, startOfMonth, endOfMonth, getDate } from 'date-fns';
import { HourRange, DayShift } from '../types';
import { MINUTES_PER_HOUR, INTERVALS_PER_HOUR } from '../types';
import { useStableDateFilter, useStableArray, useDataReadyState, useComputationKey } from '@/hooks/ui';

interface UseCalendarLogicProps {
  currentDisplayMonth: Date;
  displayOnlyDay?: Date | null;
  hidePastDays?: boolean;
  displayStartTime?: Date;
  displayEndTime?: Date;
  // Time range control props
  show24Hours?: boolean;
  // Shift data for shift-aware time range calculation
  shifts?: DayShift[];
  // Flag to enable/disable shift-aware behavior
  enableShiftAwareTimeRange?: boolean;
  // === INFINITE SCROLL SUPPORT ===
  /** Override days array with external infinite scroll date range */
  infiniteScrollDays?: Date[];
  /** Whether infinite scroll mode is active */
  isInfiniteScrollMode?: boolean;
}

export const useCalendarLogic = ({
  currentDisplayMonth,
  displayOnlyDay,
  hidePastDays = false,
  displayStartTime,
  displayEndTime,
  show24Hours = false,
  shifts = [],
  enableShiftAwareTimeRange = true,
  infiniteScrollDays,
  isInfiniteScrollMode = false
}: UseCalendarLogicProps) => {
  // Enhanced logging throttle with cache for performance
  const warningLoggedRef = useRef<Set<string>>(new Set());
  const computationCacheRef = useRef<Map<string, any>>(new Map());
  
  const logThrottledWarning = useCallback((key: string, message: string, data?: any) => {
    // Removed development logging for production build
  }, []);

  // Stabilize input data to prevent unnecessary recalculations
  const stableShifts = useStableArray(shifts || []);
  
  // Check if all required data is ready for computation
  const dataReadyState = useDataReadyState({
    'currentDisplayMonth': !!currentDisplayMonth,
    'shiftsLoaded': stableShifts !== null
  });
  
  // Create computation key for caching
  const computationKey = useComputationKey({
    currentDisplayMonth: currentDisplayMonth?.getTime(),
    displayOnlyDay: displayOnlyDay?.getTime(),
    shiftsCount: stableShifts.length,
    show24Hours,
    enableShiftAwareTimeRange,
    hasDisplayTimes: !!(displayStartTime && displayEndTime)
  });
  
  
  // Calculate stable date filter for shift analysis based on current display period
  const dateFilter = useMemo(() => {
    if (!currentDisplayMonth) {
      return null;
    }

    // Create stable date references by using time values as keys
    const monthKey = `${currentDisplayMonth.getFullYear()}-${currentDisplayMonth.getMonth()}`;
    const dayKey = displayOnlyDay ? `${displayOnlyDay.getFullYear()}-${displayOnlyDay.getMonth()}-${displayOnlyDay.getDate()}` : null;
    
    let startDate: Date, endDate: Date;
    
    if (displayOnlyDay) {
      // For single day view, filter to just that day
      startDate = startOfDay(displayOnlyDay);
      endDate = startOfDay(addDays(displayOnlyDay, 1));
    } else {
      // For month view, filter to current month
      startDate = startOfMonth(currentDisplayMonth);
      endDate = endOfMonth(currentDisplayMonth);
    }
    
    return { startDate, endDate };
  }, [
    currentDisplayMonth?.getTime(), // Use timestamp for stable comparison
    displayOnlyDay?.getTime()       // Use timestamp for stable comparison
  ]);

  // Create stable date filter object
  const stableDateFilter = useStableDateFilter(
    dateFilter?.startDate || new Date(), 
    dateFilter?.endDate || new Date()
  );

  // Simple fallback time range when no specific times are provided
  const fallbackTimeRange = useMemo(() => {
    const baseDate = new Date();
    const startTime = new Date(baseDate);
    const endTime = new Date(baseDate);
    
    if (show24Hours) {
      startTime.setHours(0, 0, 0, 0);
      endTime.setHours(23, 59, 59, 999);
    } else {
      // Default to full 24 hours: midnight to 23:59
      startTime.setHours(0, 0, 0, 0);
      endTime.setHours(23, 59, 0, 0);
    }
    
    return {
      effectiveDisplayStartTime: startTime,
      effectiveDisplayEndTime: endTime,
      totalDisplayHours: show24Hours ? 24 : 24
    };
  }, [show24Hours]);

  // Calculate effective display times with stable dependencies
  const { effectiveDisplayStartTime, effectiveDisplayEndTime, totalDisplayHours } = useMemo(() => {
    // Early return if data not ready
    if (!dataReadyState.isReady) {
      const defaultStart = new Date();
      defaultStart.setHours(0, 0, 0, 0);
      const defaultEnd = new Date();
      defaultEnd.setHours(23, 59, 0, 0);
      return {
        effectiveDisplayStartTime: defaultStart,
        effectiveDisplayEndTime: defaultEnd,
        totalDisplayHours: 24
      };
    }
    
    // If explicit display times are provided, use them (override behavior)
    if (displayStartTime && displayEndTime) {
      const startHours = getHours(displayStartTime);
      const startMinutes = getMinutes(displayStartTime);
      const endHours = getHours(displayEndTime);
      const endMinutes = getMinutes(displayEndTime);
      
      let totalHours = (endHours + endMinutes / 60) - (startHours + startMinutes / 60);
      if (totalHours <= 0) {
        totalHours += 24; // Handle overnight periods
      }
      
      return {
        effectiveDisplayStartTime: displayStartTime,
        effectiveDisplayEndTime: displayEndTime,
        totalDisplayHours: totalHours
      };
    }


    // Use fallback with smarter logging - only log when we expect display times but they're missing
    if (dataReadyState.isReady && displayStartTime === null && displayEndTime === null) {
      logThrottledWarning(
        `display-times-null-${computationKey}`,
        'useCalendarLogic: Display times are null, using 24-hour fallback',
        {
          dataReady: dataReadyState.isReady,
          hasDisplayStartTime: !!displayStartTime,
          hasDisplayEndTime: !!displayEndTime,
          displayStartType: typeof displayStartTime,
          displayEndType: typeof displayEndTime
        }
      );
    }
    
    return {
      effectiveDisplayStartTime: fallbackTimeRange.effectiveDisplayStartTime,
      effectiveDisplayEndTime: fallbackTimeRange.effectiveDisplayEndTime,
      totalDisplayHours: fallbackTimeRange.totalDisplayHours
    };
  }, [
    computationKey,
    dataReadyState.isReady,
    displayStartTime, 
    displayEndTime,
    show24Hours,
    fallbackTimeRange.effectiveDisplayStartTime,
    fallbackTimeRange.effectiveDisplayEndTime,
    logThrottledWarning
  ]);

  // Calculate days to display
  const allDaysInSelectedMonth = useMemo(() => {
    // === INFINITE SCROLL MODE ===
    // When infinite scroll is active, use the external days array
    if (isInfiniteScrollMode && infiniteScrollDays && infiniteScrollDays.length > 0) {
      return infiniteScrollDays;
    }

    if (displayOnlyDay) {
      return [displayOnlyDay];
    }

    // Month view
    const start = startOfMonth(currentDisplayMonth);
    const end = addDays(start, getDaysInMonth(currentDisplayMonth) - 1);
    return eachDayOfInterval({ start, end });
  }, [currentDisplayMonth, displayOnlyDay, isInfiniteScrollMode, infiniteScrollDays]);

  // Filter days based on hidePastDays
  const daysInSelectedMonth = useMemo(() => {
    if (hidePastDays && !displayOnlyDay) {
      const today = startOfDay(new Date());
      return allDaysInSelectedMonth.filter(day => !isBefore(startOfDay(day), today));
    }
    return allDaysInSelectedMonth;
  }, [allDaysInSelectedMonth, hidePastDays, displayOnlyDay]);

  // Organize stable shifts by day
  const shiftsByDay = useCallback((shifts: DayShift[] | undefined | null) => {
    const shiftsMap: { [key: number]: DayShift[] } = {};
    
    // Handle undefined or null shifts
    if (!shifts || !Array.isArray(shifts)) {
      return shiftsMap;
    }
    
    const allDaySet = new Set(allDaysInSelectedMonth.map(day => getDate(day)));
    const monthShifts = shifts.filter(shift => 
      shift && shift.startTime && allDaySet.has(getDate(shift.startTime))
    );

    for (const shift of monthShifts) {
      const startDay = getDate(shift.startTime);
      shiftsMap[startDay] ??= [];
      shiftsMap[startDay].push(shift);
    }
    
    return shiftsMap;
  }, [allDaysInSelectedMonth]);

  return {
    effectiveDisplayStartTime,
    effectiveDisplayEndTime,
    totalDisplayHours,
    allDaysInSelectedMonth,
    daysInSelectedMonth,
    shiftsByDay
  };
};